import { ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import type { SortOrder } from './types';

/**
 * Canonical sortable column header. Reconciles the two near-identical copies
 * that were living in `files-viewer/src/components/billin./Controls.tsx`
 * and `dashboard/src/pages/billing/invoice./Controls.tsx` - the dashboard
 * copy exists only because files-viewer components aren't importable across
 * apps, and its own comment says so.
 *
 * Differences resolved: the neutral (unsorted) affordance is now an inline
 * heroicon rather than each app's local `assets/icons/sort.svg`, since a
 * shared package can't reach into an app's assets.
 *
 * `aria-sort` belongs on a semantic `<th scope="col">`, which `<DataTable>`
 * sets on its `A11yHeaderRow` inside the body table - this button is only the
 * visual/interactive affordance. But the button lives in the *presentational*
 * header table, where no `aria-sort` is announced when it takes focus, so an
 * active button also appends its sorted state to its own accessible name via
 * an `sr-only` suffix - a keyboard user tabbing onto it hears the state
 * without ever touching the table.
 */
export interface DataTableSortHeaderProps<K extends string> {
    label: string;
    /** This column's sort key. */
    column: K;
    /** The currently sorted key. */
    sortBy: K;
    sortOrder: SortOrder;
    onSort: (column: K) => void;
    className?: string;
}

export function DataTableSortHeader<K extends string>({
    label,
    column,
    sortBy,
    sortOrder,
    onSort,
    className
}: DataTableSortHeaderProps<K>) {
    const active = sortBy === column;
    const tone = active ? 'text-gray-800' : 'text-neutral-500 hover:text-gray-700';
    return (
        <button
            type="button"
            onClick={() => onSort(column)}
            className={`flex items-center gap-1 min-w-0 max-w-full select-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 rounded ${tone} ${className ?? ''}`}
        >
            {/* Truncates at narrow widths instead of squashing neighbours. */}
            <span className="truncate">{label}</span>
            {active && (
                <span className="sr-only">
                    {sortOrder === 'asc' ? ', sorted ascending' : ', sorted descending'}
                </span>
            )}
            {active ? (
                sortOrder === 'asc' ? (
                    <ChevronUpIcon
                        className="w-4 h-4 flex-shrink-0 text-gray-800"
                        aria-hidden="true"
                    />
                ) : (
                    <ChevronDownIcon
                        className="w-4 h-4 flex-shrink-0 text-gray-800"
                        aria-hidden="true"
                    />
                )
            ) : (
                <ChevronUpDownIcon
                    className="w-4 h-4 flex-shrink-0 text-gray-400"
                    aria-hidden="true"
                />
            )}
        </button>
    );
}
